import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { FunnelReportsDashboardData, FunnelReportsScopeRollup } from '../types';
import { FUNNEL_REPORTS_TEST_IDS } from '../testIds';

/**
 * "Warnings & data trust" — the ONE place that spells out every trust problem
 * of the current window (user review feedback: warnings live at the bottom,
 * not as an alert pile on top; affected rows are marked inline instead).
 * Each entry answers three operator questions: WHICH scope, WHAT the warning
 * means, and WHAT TO DO so the data becomes trustworthy.
 */

interface WarningEntry {
  key: string;
  severity: 'error' | 'warning';
  scope: string;
  meaning: string;
  fix: string;
}

function scopeLabel(row: FunnelReportsScopeRollup): string {
  const identity = row.identity;
  const placement = identity?.placementName ?? row.alias;
  if (row.rollupLevel === 'onboarding_version') {
    const onboarding = identity?.onboardingName ?? row.onboardingId ?? 'unknown onboarding';
    const version = identity?.versionName ?? row.versionId ?? 'unknown version';
    return `${placement} · ${onboarding} / ${version}`;
  }
  return placement;
}

const DQ_CRITICAL_MEANING =
  'Event chains for this scope failed data-quality checks (missing funnel steps, duplicates, or test/fixture traffic). The metrics can be wrong — do not use them for decisions.';
const DQ_CRITICAL_FIX =
  'Open the Data Quality report section to see the exact issues; exclude test/fixture traffic or repair the event chain, then run Refresh — the severity returns to "ok" once checks pass.';
const DQ_WARNING_MEANING =
  'Some data-quality checks flagged this scope; treat its metrics as approximate.';
const DQ_WARNING_FIX =
  'Check the Data Quality report section for the flagged issues and refresh after fixing; warnings usually come from partial event chains in the window.';
const IDENTITY_FIX =
  'Set display names on the Web Placement (placement name; funnel/version names are taken from the placement config at publish time) and republish — reports resolve names automatically on the next load.';

export function deriveReportWarnings(
  dashboard: FunnelReportsDashboardData | null | undefined,
): WarningEntry[] {
  const entries: WarningEntry[] = [];
  const rollups = [
    ...(dashboard?.aliasSummaries ?? []),
    ...(dashboard?.aliasOnboardingSummaries ?? []),
  ];

  for (const row of rollups) {
    const key = `${row.rollupLevel}-${row.alias}-${row.onboardingId ?? ''}-${row.versionId ?? ''}`;
    const severity = (row.dataQualitySeverity || '').toLowerCase();
    if (severity === 'critical') {
      entries.push({
        key: `dq-${key}`,
        severity: 'error',
        scope: scopeLabel(row),
        meaning: DQ_CRITICAL_MEANING,
        fix: DQ_CRITICAL_FIX,
      });
    } else if (severity === 'warning' || severity === 'warn') {
      entries.push({
        key: `dq-${key}`,
        severity: 'warning',
        scope: scopeLabel(row),
        meaning: DQ_WARNING_MEANING,
        fix: DQ_WARNING_FIX,
      });
    }

    if (row.identity?.identityWarnings?.length) {
      entries.push({
        key: `identity-${key}`,
        severity: 'warning',
        scope: scopeLabel(row),
        meaning: `Display names are incomplete (${row.identity.identityWarnings.join('; ')}); the row falls back to raw ids.`,
        fix: IDENTITY_FIX,
      });
    }
  }

  // AC-H-8: the money-reporting-policy fail-closed state is a project-level
  // condition — one entry naming the fix, derived from the typed reasons the
  // campaign rows already carry (no extra backend field needed).
  const policyBlocked = (dashboard?.campaignEconomics ?? []).some(row =>
    row.ltvValueBasis?.contribution.status === 'unavailable'
    && row.ltvValueBasis.contribution.unavailableReasons?.some(reason =>
      reason.startsWith('money_reporting_policy_missing')));
  if (policyBlocked) {
    entries.push({
      key: 'money-reporting-policy-missing',
      severity: 'warning',
      scope: 'Paid acquisition — cross-currency money aggregates',
      meaning:
        'Money reporting policy is not configured for this project. Contribution and projected-value aggregates that need cross-currency conversion are withheld, not zero.',
      fix:
        'Register a money-reporting policy for this project — the withheld numbers appear automatically after the next nightly run.',
    });
  }

  return entries.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1));
}

interface ReportWarningsSectionProps {
  dashboard: FunnelReportsDashboardData | null | undefined;
}

export const ReportWarningsSection: React.FC<ReportWarningsSectionProps> = ({ dashboard }) => {
  const entries = React.useMemo(() => deriveReportWarnings(dashboard), [dashboard]);
  if (!entries.length) return null;

  const criticalCount = entries.filter(entry => entry.severity === 'error').length;

  return (
    <Accordion
      defaultExpanded={criticalCount > 0}
      data-testid={FUNNEL_REPORTS_TEST_IDS.warningsSection}
      // A11y: one heading per card at h5 — the page title is an h4 and MUI's
      // own heading wrapper defaults to h3, which skipped a level both ways.
      slotProps={{ heading: { component: 'h5' } }}
      sx={{
        borderRadius: 4,
        // DESIGN.md surface radius is 16px; MUI's rounded-accordion rule sets
        // 4px with higher specificity, so the selector is matched.
        '&.MuiAccordion-rounded:first-of-type, &.MuiAccordion-rounded:last-of-type': {
          borderRadius: 4,
        },
      }}
    >
      {/* C1: «Every report section is a card whose header is the toggle: title
          1.25rem/500 left, chevron right». Four collapsible blocks on this page
          had four header typographies (1.25/500, 1rem/700, 0.875/700,
          0.875/500) and two paddings; they share one treatment now. */}
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" component="span" sx={{ fontWeight: 500 }}>Warnings &amp; data trust</Typography>
          {criticalCount > 0 && <Chip label={`${criticalCount} critical`} color="error" size="small" />}
          <Chip label={`${entries.length} total`} size="small" variant="outlined" />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {entries.map(entry => (
            <Alert key={entry.key} severity={entry.severity} data-testid={FUNNEL_REPORTS_TEST_IDS.warningsItem(entry.key)}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{entry.scope}</Typography>
              <Typography variant="body2">{entry.meaning}</Typography>
              <Typography variant="body2" color="text.secondary">Fix: {entry.fix}</Typography>
            </Alert>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
